import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import { DetailsPanelId, PageId, PageRegistrationConfig } from "../../constants/pluginConstants";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { optimizedRetryOption } from "../../helpers/resourceHelper";
import { AnalyticsDataPage, LinkAnalyticsData } from "../../hooks/useAnalytics";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";
import { Resource, ResourceLink } from "./ResourceLink";

export interface ExaInfraLinkProps {
  vmClusterId: string|undefined;
  location: string;
  analytics: AnalyticsDataPage;
}

const registrationIds = PageRegistrationConfig[PageId.EXAINFRA_DETAILS].map(config => config.key);

export const ExaInfraLink = ({
  vmClusterId,
  location,
  analytics,
}: ExaInfraLinkProps): JSX.Element => {
  const vmClusterIdComps = parseId(vmClusterId);
  const vmClusterResGroup = decodeURIComponent(vmClusterIdComps?.resourceGroup || "");
  const vmClusterResourceName = decodeURIComponent(vmClusterIdComps?.resourceName || "");
  const { response: responseVmCluster, error: errorVmCluster, loading: loadingVmCluster } = useQueryCall({
    wait: !vmClusterId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getCloudVmCluster,
    options: {
      args: {
        subscriptionId: vmClusterIdComps?.subscriptionId || "",
        resourceGroupName: vmClusterResGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        cloudVmClusterName: vmClusterResourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });
  const vmCluster = responseVmCluster?.data;

  const infraIdComps = parseId(vmCluster?.cloudExadataInfrastructureId);
  const infraResGroup = decodeURIComponent(infraIdComps?.resourceGroup || "");
  const infraResourceName = decodeURIComponent(infraIdComps?.resourceName || "");
  const { response: responseInfra, error: errorInfra, loading: loadingInfra } = useQueryCall({
    wait: !vmCluster?.id,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getCloudExadataInfrastructure,
    options: {
      args: {
        subscriptionId: infraIdComps?.subscriptionId || "",
        resourceGroupName: infraResGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        cloudExadataInfrastructureName: infraResourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });
  const infra = responseInfra?.data;

  return (
    <ResourceLink
      resource={infra as Resource}
      navigation={{
        navigationKey: registrationIds[0],
        panelPath: PageRegistrationConfig[PageId.EXAINFRA_DETAILS][0].panelPath,
      }}
      analytics={{
        target: { pageId: PageId.EXAINFRA_DETAILS, panelId: DetailsPanelId.OVERVIEW },
        source: { pageId: analytics.pageId, panelId: analytics.panelId },
      } as LinkAnalyticsData}
      error={errorVmCluster || errorInfra}
      loading={loadingVmCluster || loadingInfra}
    />
  );
};
